// ElevenLabs text-to-speech for the AI participant's spoken voice. Browser-direct
// (the TTS endpoint sends open CORS headers; team-internal BYOK key, so no proxy
// or token-minting backend is needed — same trust model as the STT key). We fetch
// a short MP3 for a card's text and play it through an <audio> element so we can
// route it to a chosen output device (setSinkId) — e.g. a BlackHole virtual cable
// wired into a Meet/Zoom tab's mic so remote participants hear the AI too.

const TTS_ENDPOINT = 'https://api.elevenlabs.io/v1/text-to-speech'
// Flash v2.5: ElevenLabs' lowest-latency model (~75 ms), ideal for short
// conversational lines where the voice should start almost immediately.
const DEFAULT_MODEL_ID = 'eleven_flash_v2_5'

export interface TtsConfig {
  apiKey: string
  voiceId: string
  modelId?: string
}

/** Fetch the spoken audio for `text` as an MP3 blob. Cheap to prefetch ahead of
 *  claiming the floor so playback can start the instant the PING lands. */
export async function synthesize(
  text: string,
  cfg: TtsConfig,
  signal?: AbortSignal
): Promise<Blob> {
  const voice = encodeURIComponent(cfg.voiceId)
  const url =
    `${TTS_ENDPOINT}/${voice}/stream` +
    '?optimize_streaming_latency=3&output_format=mp3_44100_128'
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'xi-api-key': cfg.apiKey,
      'content-type': 'application/json',
      accept: 'audio/mpeg'
    },
    body: JSON.stringify({ text, model_id: cfg.modelId ?? DEFAULT_MODEL_ID }),
    signal
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(
      `ElevenLabs TTS ${res.status}${detail ? `: ${detail.slice(0, 140)}` : ''}`
    )
  }
  return res.blob()
}

// setSinkId lives on HTMLMediaElement in newer DOM libs; type it locally so we
// can feature-detect without an `any`.
type AudioWithSink = HTMLAudioElement & {
  setSinkId?: (id: string) => Promise<void>
}

export interface Utterance {
  /** Resolves when playback finishes OR is stopped (never rejects). */
  done: Promise<void>
  /** Stop playback and release the audio element + object URL. */
  stop(): void
}

/** Play an MP3 blob, optionally routed to a specific output device. */
export function play(blob: Blob, outputDeviceId?: string): Utterance {
  const url = URL.createObjectURL(blob)
  const audio = new Audio() as AudioWithSink
  audio.src = url

  let settle: () => void = () => {}
  const done = new Promise<void>((resolve) => {
    settle = resolve
  })

  let stopped = false
  function cleanup(): void {
    if (stopped) return
    stopped = true
    audio.pause()
    audio.src = ''
    URL.revokeObjectURL(url)
    settle()
  }
  audio.onended = cleanup
  audio.onerror = cleanup

  // Route to the chosen sink (e.g. BlackHole) when supported + requested, then
  // play. A rejected setSinkId (unknown device) falls back to the default sink.
  const routed =
    outputDeviceId && typeof audio.setSinkId === 'function'
      ? audio.setSinkId(outputDeviceId).catch(() => {})
      : Promise.resolve()
  void routed.then(() => {
    if (!stopped) void audio.play().catch(() => cleanup())
  })

  return { done, stop: cleanup }
}
